import React, { useState, useEffect } from 'react'
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  Tabs,
  TabList,
  Tab,
} from '@mui/joy'
import {
  SquaresFour as Dashboard,
  FileText as Description,
  Receipt,
  CreditCard as Payment,
  Plus as Add,
  Gear as Settings,
} from '@phosphor-icons/react'
import { estimatesApi, invoicesApi } from './lib/billingApi'

export default function BillingApp() {
  const [activeTab, setActiveTab] = useState('overview')
  const location = useLocation()
  const navigate = useNavigate()

  // Lightweight counts for the tab badges (cached; the tab pages fetch details)
  const { data: estimateCount } = useQuery({
    queryKey: ['billing-estimates-badge-count'],
    queryFn: () => estimatesApi.list({}).then((r) => r.count),
    staleTime: 60_000,
  })
  const { data: invoiceCount } = useQuery({
    queryKey: ['billing-invoices-badge-count'],
    queryFn: () => invoicesApi.list({}).then((r) => r.count),
    staleTime: 60_000,
  })

  // Handle routing integration
  useEffect(() => {
    const path = location.pathname

    if (path.includes('/estimates')) {
      setActiveTab('estimates')
    } else if (path.includes('/invoices')) {
      setActiveTab('invoices')
    } else if (path.includes('/recurring')) {
      // Recurring invoices render inside this shell but have their own sidebar
      // entry - no workspace tab should light up.
      setActiveTab('recurring')
    } else if (path.includes('/payments')) {
      setActiveTab('payments')
    } else if (path.includes('/settings')) {
      setActiveTab('settings')
    } else {
      setActiveTab('overview')
    }
  }, [location.pathname])

  const handleTabChange = (event: React.SyntheticEvent | null, value: string | number | null) => {
    const newTab = value as string
    setActiveTab(newTab)

    // Navigate to the appropriate route (relative to current location)
    switch (newTab) {
      case 'overview':
        navigate('.')
        break
      case 'estimates':
        navigate('estimates')
        break
      case 'invoices':
        navigate('invoices')
        break
      case 'payments':
        navigate('payments')
        break
      case 'settings':
        navigate('settings')
        break
    }
  }

  const tabSx = {
    borderRadius: 'lg',
    flex: { xs: '1 1 45%', sm: '0 0 auto' },
    fontSize: { xs: 'sm', sm: 'md' },
    px: { xs: 1.25, sm: 2 },
    whiteSpace: 'nowrap',
    '&[aria-selected="true"]': {
      bgcolor: 'background.surface',
      boxShadow: 'sm',
      color: 'primary.plainColor',
      fontWeight: 'lg',
    },
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: 'background.body',
      }}
    >
      {/* Workspace header */}
      <Box sx={{ px: { xs: 1.5, md: 3 }, pt: { xs: 1.5, md: 2.5 } }}>
        <Stack
          direction="row"
          sx={{ gap: 1.5, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', mb: 2 }}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 300px' }}>
            <Typography level="h2" component="h1" sx={{ fontSize: { xs: '1.25rem', md: undefined } }}>
              Billing
            </Typography>
            <Typography level="body-sm" color="neutral" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Monitor project revenue, receivables and payments without leaving the billing workspace.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' }, '& > *': { flex: { xs: 1, sm: '0 0 auto' } } }}>
            <Button
              component={Link}
              to="/operations/billing/invoices/new"
              startDecorator={<Add />}
              variant="solid"
              color="primary"
            >
              New Invoice
            </Button>
            <Button
              component={Link}
              to="/operations/billing/estimates/new"
              startDecorator={<Description />}
              variant="outlined"
              color="primary"
            >
              New Estimate
            </Button>
          </Stack>
        </Stack>

        {/* Workspace tabs */}
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ bgcolor: 'transparent' }}>
          <TabList
            disableUnderline
            sx={{
              p: 0.5,
              gap: 0.5,
              borderRadius: 'xl',
              bgcolor: 'background.level1',
              flexWrap: 'wrap',
            }}
          >
            <Tab value="overview" disableIndicator sx={tabSx}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Dashboard size={18} />
                <span>Overview</span>
              </Stack>
            </Tab>
            <Tab value="estimates" disableIndicator sx={tabSx}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Description size={18} />
                <span>Estimates</span>
                {typeof estimateCount === 'number' && (
                  <Chip size="sm" variant="soft" color="primary">{estimateCount}</Chip>
                )}
              </Stack>
            </Tab>
            <Tab value="invoices" disableIndicator sx={tabSx}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Receipt size={18} />
                <span>Invoices</span>
                {typeof invoiceCount === 'number' && (
                  <Chip size="sm" variant="soft" color="neutral">{invoiceCount}</Chip>
                )}
              </Stack>
            </Tab>
            <Tab value="payments" disableIndicator sx={tabSx}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Payment size={18} />
                <span>Payments</span>
              </Stack>
            </Tab>
            <Tab value="settings" disableIndicator sx={tabSx}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Settings size={18} />
                <span>
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Billing&nbsp;</Box>Settings
                </span>
              </Stack>
            </Tab>
          </TabList>
        </Tabs>
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflowX: 'hidden', overflowY: 'auto', minWidth: 0 }}>
        <Outlet />
      </Box>
    </Box>
  )
}
